import React from 'react';

export const ConfidenceMeter: React.FC<{ confidence: number; gradient: { from: string; to: string } }> = ({ confidence, gradient }) => (
  <div className="flex items-center gap-2 mb-3">
    <span className="text-xs font-medium text-gray-600">AI Confidence</span>
    <div className="flex-1 bg-gray-200 rounded-full h-2">
      <div 
        className="h-2 rounded-full transition-all duration-500"
        style={{ 
          width: `${confidence}%`,
          background: `linear-gradient(to right, ${gradient.from}, ${gradient.to})`
        }}
      />
    </div>
    <span className="text-xs font-bold text-gray-700">{confidence}%</span>
  </div>
);

export const PriorityBubble: React.FC<{ label: string; priority: number; color: string }> = ({ label, priority, color }) => {
  const size = Math.max(40, Math.min(80, priority));
  return (
    <div className="flex items-center gap-3 p-2">
      <div 
        className={`rounded-full ${color} flex items-center justify-center text-white font-bold text-xs shadow-md`}
        style={{ width: `${size}px`, height: `${size}px` }}
      >
        {priority}
      </div>
      <span className="text-sm text-gray-700 flex-1">{label}</span>
    </div>
  );
};

export const RiskIndicator: React.FC<{ level: 'low' | 'medium' | 'high'; label: string }> = ({ level, label }) => {
  const colors = {
    low: 'bg-green-500',
    medium: 'bg-yellow-500', 
    high: 'bg-red-500'
  };
  const textColors = {
    low: 'text-green-700',
    medium: 'text-yellow-700',
    high: 'text-red-700'
  };
  const bgColors = {
    low: 'bg-green-50',
    medium: 'bg-yellow-50',
    high: 'bg-red-50'
  };
  
  return (
    <div className={`flex items-center gap-2 p-2 rounded-md ${bgColors[level]}`}>
      <div className={`w-3 h-3 rounded-full ${colors[level]} animate-pulse`} />
      <span className={`text-sm font-medium ${textColors[level]}`}>{label}</span>
    </div>
  );
};

export const EngagementHeatmap: React.FC<{ score: number }> = ({ score }) => {
  const getHeatColor = (value: number) => {
    if (value >= 80) return 'bg-green-500';
    if (value >= 60) return 'bg-yellow-500';
    if (value >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const weeks = Array.from({ length: 12 }, (_, i) => ({
    week: i + 1,
    engagement: Math.max(20, score + (Math.random() - 0.5) * 30)
  }));

  return (
    <div className="space-y-2">
      <h5 className="text-xs font-medium text-gray-600">Engagement History (12 weeks)</h5>
      <div className="flex gap-1">
        {weeks.map((week) => (
          <div
            key={week.week}
            className={`w-4 h-4 rounded-sm ${getHeatColor(week.engagement)} opacity-80 hover:opacity-100 transition-opacity`}
            title={`Week ${week.week}: ${Math.round(week.engagement)}% engagement`}
          />
        ))}
      </div>
    </div>
  );
}; 